import React, { useState, useEffect, useCallback, useRef } from 'react';
import classnames from 'classnames';
import Autosuggest from 'react-autosuggest';
import InfiniteScroll from 'react-infinite-scroll-component';
import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import { debounce } from 'throttle-debounce';
import { SearchIcon, Loader, Scrollbar, PlusInCircleRedIcon } from 'components';
import ConditionalWrapper from 'components/ConditionalWrapper';

import './search.scss';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function getSuggestionValue(suggestion) {
  return suggestion;
}

function renderSuggestion(suggestion, { query }) {
  const matches = AutosuggestHighlightMatch(suggestion, query);
  const parts = AutosuggestHighlightParse(suggestion, matches);
  return (
    <span className="react-autosuggest__suggestion-text">
      {parts.map((part, index) => (
        <span
          className={classnames({
            'react-autosuggest__suggestion-match': part.highlight,
          })}
          key={index}
        >
          {part.text}
        </span>
      ))}
    </span>
  );
}

function renderSuggestionStudies(suggestion, { query }) {
  const matches = AutosuggestHighlightMatch(suggestion, query);
  const parts = AutosuggestHighlightParse(suggestion, matches);
  return (
    <div className="react-autosuggest__suggestion-text_studies">
      <div>
        {parts.map((part, index) => (
          <span
            className={classnames({
              'react-autosuggest__suggestion-match': part.highlight,
            })}
            key={index}
          >
            {part.text}
          </span>
        ))}
      </div>
      <PlusInCircleRedIcon />
    </div>
  );
}

export default function SearchSimpleField(props = {}) {
  const {
    onInputChange,
    onInputDataChange,
    onChange: onSearchChange,
    // onInputDataChange: onSearchChange,
    value: initialValue = '',
    onSuggestionStudies = false,
    onSuggestionSelectedCallback = Function.prototype,
    searchInstrument = Function.prototype,
    showIcon = true,
    noResetValue,
    withSpaces,
    fieldClassName,
    withBlankRender,
    withInfinityScroll,
    ...propsToPass
  } = props;

  const [value, setValue] = useState(initialValue);
  const [items, setItems] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [fetchedItems, setFetchedItems] = useState([]);
  const prevFetching = usePrevious(isFetching);
  const prevValue = usePrevious(value);

  useEffect(() => {
    if (value === '') {
      setFetchedItems([]);
      setIsFetching(false);
    }
    setHasMore(true);
  }, [value, items]);

  useEffect(() => {
    if (noResetValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    prevFetching ? setStopLoading(true) : setStopLoading(false);
  }, [isFetching]);

  const onSuggestionsFetchRequested = async ({ value, next }) => {
    const escapedValue = value.trim().replace(/\W/g, '');
    const spacesValue = value.replace(' ', '%20');

    if (!withBlankRender && escapedValue === '') {
      return setItems([]);
    }

    setIsFetching(true);

    let fetchedItems = '';

    if (withSpaces) {
      fetchedItems = await searchInstrument(spacesValue, next);
    } else {
      fetchedItems = await searchInstrument(escapedValue, next);
    }

    setFetchedItems(fetchedItems);
    setIsFetching(false);
    setItems(fetchedItems || []);
  };

  const debounceOnChange = useCallback(
    debounce(500, onSuggestionsFetchRequested),
    [],
  );

  // Reset the instruments
  const onSuggestionsClearRequested = () => {
    setStopLoading(false);
    setItems([]);
  };

  const renderInputComponent = (inputProps) => (
    <div className="input-component">
      <input {...inputProps} />
      {!isFetching && showIcon && (
        <SearchIcon className="search-component__search-icon" />
      )}
      {isFetching && (
        <Loader
          transparent={true}
          isShow={true}
          className="search-component__loader"
          centeredLoader
          spinnerStyles={{
            width: '15px',
            height: '15px',
            left: 'auto',
            right: '0',
          }}
        >
          <div
            style={{ width: '15px', height: '15px', margin: '4px 0 4px 0' }}
          />
        </Loader>
      )}
    </div>
  );

  const fetchData = async () => {
    await onSuggestionsFetchRequested({ value, next: true });
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    const height =
      children && items.length
        ? items.length * 64
        : value !== '' &&
          stopLoading &&
          prevValue !== '' &&
          fetchedItems.length === 0
        ? 20
        : 0;
    const forInfinityScrollProps = withInfinityScroll && {
      scrollerProps: {
        renderer: (props) => {
          const { elementRef, ...restProps } = props;
          return (
            <div
              {...restProps}
              ref={elementRef}
              id="MyAwesomeScrollbarsScroller"
              className="MyAwesomeScrollbarsScroller"
            />
          );
        },
      },
    };

    return (
      <div
        {...containerProps}
        style={{ height: `${height}px`, paddingBottom: '10x' }}
      >
        {items.length > 0 ? (
          <Scrollbar {...forInfinityScrollProps}>
            <ConditionalWrapper
              condition={withInfinityScroll}
              children={children}
              wrapper={(children) => (
                <InfiniteScroll
                  dataLength={items.length}
                  next={fetchData}
                  hasMore={hasMore}
                  scrollableTarget={'MyAwesomeScrollbarsScroller'}
                >
                  {children}
                </InfiniteScroll>
              )}
            />
          </Scrollbar>
        ) : (
          'No results found for your request'
        )}
      </div>
    );
  };

  // Select and throw instrument for Chart component
  const onSuggestionSelected = (event, { suggestion }) => {
    onSearchChange(suggestion);
    onInputDataChange(suggestion);
    onSuggestionSelectedCallback(suggestion);

    if (!noResetValue) {
      setValue('');
    }
  };

  const onChange = (event, { newValue }) => {
    if (newValue === '') {
      setStopLoading(false);
    }
    setValue(newValue);
    onSearchChange(newValue);
  };

  const shouldRenderSuggestions = () => {
    return true;
  };

  return (
    <div className="search-component">
      <Autosuggest
        suggestions={items}
        onSuggestionsFetchRequested={(suggestions) =>
          debounceOnChange(suggestions)
        }
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={
          onSuggestionStudies ? renderSuggestionStudies : renderSuggestion
        }
        renderInputComponent={renderInputComponent}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          value,
          className: 'search-component__search-field',
          ...propsToPass,
          onChange,
        }}
      />
    </div>
  );
}
